import { ReactNode, TransitionEvent, useEffect, useRef, useState } from "react";

import { cn } from "@/common/utils/css.utils";

interface AnimatedBannerProps {
  enter?: string;
  enterFrom?: string;
  enterTo?: string;
  entered?: string;
  leave?: string;
  leaveFrom?: string;
  leaveTo?: string;
  panels: ReactNode[];
  timeout?: number;
}

const AnimatedBanner = ({
  enter,
  enterFrom,
  enterTo,
  entered,
  leave,
  leaveFrom,
  leaveTo,
  panels,
  timeout,
}: AnimatedBannerProps) => {
  const [index, setIndex] = useState(0);
  const [leaving, setLeaving] = useState(false);
  // false = sitting on the *From class, true = transitioned to the *To class.
  const [atTarget, setAtTarget] = useState(false);

  const timerRef = useRef<number | null>(null);

  // After landing on a *From state, flip to *To one frame later so the browser
  // registers the start values and animates the change (headlessui did this internally).
  useEffect(() => {
    if (atTarget) {
      return;
    }

    const raf = requestAnimationFrame(() => setAtTarget(true));

    return () => cancelAnimationFrame(raf);
  }, [index, leaving, atTarget]);

  // Cleanup pending timer on unmount.
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const handleTransitionEnd = (event: TransitionEvent<HTMLDivElement>) => {
    // transitionend fires once per property; act on opacity only (every caller animates it).
    if (event.propertyName !== "opacity" || !atTarget) {
      return;
    }

    if (leaving) {
      // Leave done → advance and re-enter. setTimeout(0) because changing state
      // synchronously here randomly kills the animation on mobile.
      timerRef.current = window.setTimeout(() => {
        setIndex(prev => (prev < panels.length - 1 ? prev + 1 : 0));
        setLeaving(false);
        setAtTarget(false);
      });
    } else {
      // Enter done → hold, then start leaving.
      timerRef.current = window.setTimeout(() => {
        setLeaving(true);
        setAtTarget(false);
      }, timeout);
    }
  };

  const className = leaving
    ? cn(leave, atTarget ? leaveTo : leaveFrom)
    : cn(enter, atTarget ? cn(enterTo, entered) : enterFrom);

  return (
    // key on index so each panel mounts fresh at its enterFrom position instead of
    // animating across from the previous panel's leaveTo (which read as entering from the right).
    <div
      key={index}
      className={className}
      onTransitionEnd={handleTransitionEnd}
    >
      {panels[index]}
    </div>
  );
};

export default AnimatedBanner;
